import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router'
import { QueryClient, useIsRestoring } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { createDexiePersister } from './lib/dexieQueryPersister'
import { refreshServerDataAfterRestore } from './lib/refreshServerData'
import { useAuth } from './hooks/useAuth'
import { useOfflineSync } from './hooks/useOfflineSync'
import { ToastProvider } from './context/ToastProvider'
import { I18nProvider } from './i18n/I18nProvider'
import { fetchCsrfCookie } from './lib/api'
import Layout from './components/layout/Layout'
import LoginPage from './pages/LoginPage'
import HomePage from './pages/HomePage'
import ProductListPage from './pages/ProductListPage'
import ProductDetailPage from './pages/ProductDetailPage'
import WizardPage from './pages/WizardPage'
import FavoritesPage from './pages/FavoritesPage'
import ComparisonPage from './pages/ComparisonPage'
import CartPage from './pages/CartPage'
import SettingsPage from './pages/SettingsPage'
import ChangePasswordPage from './pages/ChangePasswordPage'
import NotFoundPage from './pages/NotFoundPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import { OfflineBadge } from './components/common/OfflineBadge'
import { PwaUpdatePrompt } from './components/common/PwaUpdatePrompt'
import { InstallPrompt } from './components/common/InstallPrompt'
import { useOfflinePrefetch } from './hooks/useOfflinePrefetch'
import { initOfflineOperations } from './lib/offline/init'

initOfflineOperations()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      gcTime: Infinity,
      networkMode: 'offlineFirst',
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
    mutations: { networkMode: 'always' },
  },
})

const persister = createDexiePersister()

function ProtectedRoutes() {
  const { isLoggedIn, user } = useAuth()
  const isRestoring = useIsRestoring()
  const location = useLocation()
  useOfflineSync(isLoggedIn, user?.id ?? null)

  useEffect(() => {
    if (isLoggedIn && navigator.onLine) fetchCsrfCookie().catch(() => {})
  }, [isLoggedIn])
  // Prefetch every user-facing query so the React Query persister has a
  // snapshot ready for offline use — including pages the user has not
  // opened yet (VIVATISPDB-306, e.g. the Wizard master data).
  useOfflinePrefetch(isLoggedIn)

  if (isRestoring) return null

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />
  }

  // When navigating to a product from a list page, the previous location is
  // passed as state.backgroundLocation so the page beneath stays mounted.
  const backgroundLocation = (location.state as { backgroundLocation?: Location })?.backgroundLocation

  return (
    <>
      <Routes location={backgroundLocation ?? location}>
        <Route element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="products" element={<ProductListPage />} />
          <Route path="products/:id" element={<ProductDetailPage />} />
          <Route path="wizard" element={<WizardPage />} />
          <Route path="favorites" element={<FavoritesPage />} />
          <Route path="compare" element={<ComparisonPage />} />
          <Route path="cart" element={<CartPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="change-password" element={<ChangePasswordPage />} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      {/* Render product detail as overlay when coming from another page */}
      {backgroundLocation && (
        <Routes>
          <Route path="products/:id" element={<ProductDetailPage />} />
        </Routes>
      )}

      <OfflineBadge />
      <InstallPrompt />
    </>
  )
}

function App() {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister, maxAge: 1000 * 60 * 60 * 24 * 30 }}
      onSuccess={() => refreshServerDataAfterRestore(queryClient)}
    >
      <I18nProvider>
        <ToastProvider>
          <PwaUpdatePrompt />
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/*" element={<ProtectedRoutes />} />
            </Routes>
          </BrowserRouter>
        </ToastProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </I18nProvider>
    </PersistQueryClientProvider>
  )
}

export default App
